(function () {
  const SECTION_SELECTOR = "[data-sticky-video-cta]";
  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([type='hidden']):not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "iframe",
    "[tabindex]:not([tabindex='-1'])",
  ].join(", ");

  class StickyVideoCta {
    constructor(root) {
      if (!root || root.dataset.stickyVideoCtaInitialized === "true") return;

      this.root = root;
      this.video = root.querySelector("[data-sticky-video-cta-video]");
      this.trigger = root.querySelector("[data-sticky-video-cta-trigger]");
      this.stickyBar = root.querySelector(".sticky-video-cta__sticky");
      this.modal = root.querySelector("[data-sticky-video-cta-modal]");
      this.dialog = this.modal?.querySelector(".sticky-video-cta-modal__dialog");
      this.iframe = this.modal?.querySelector("[data-sticky-video-cta-iframe]");
      this.closeButtons = this.modal
        ? Array.from(this.modal.querySelectorAll("[data-sticky-video-cta-close]"))
        : [];
      this.scrollEnabled = this.trigger?.dataset.scrollEnabled === "true";
      this.scrollTarget = this.trigger?.dataset.scrollTarget || "";
      this.formUrl = this.trigger?.dataset.formUrl || this.iframe?.dataset.src || "";
      this.lastFocusedElement = null;
      this.scrollY = 0;
      this.closeTimer = null;
      this.unlockHandlersAttached = false;
      this.breakpoint = window.matchMedia("(max-width: 767px)");

      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.resumePlayback = this.resumePlayback.bind(this);
      this.forcePlay = this.forcePlay.bind(this);

      this.init();
      root.__stickyVideoCta = this;
      root.dataset.stickyVideoCtaInitialized = "true";
    }

    init() {
      if (this.trigger && !this.trigger.disabled) {
        this.trigger.addEventListener("click", this.handleTriggerClick);
      }

      this.closeButtons.forEach((button) => {
        button.addEventListener("click", this.handleCloseClick);
      });

      if (this.video) {
        this.prepareVideo();
      }

      if (this.breakpoint.addEventListener) {
        this.breakpoint.addEventListener("change", this.handleViewportChange);
      } else if (this.breakpoint.addListener) {
        this.breakpoint.addListener(this.handleViewportChange);
      }
    }

    prepareVideo() {
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.autoplay = true;
      this.video.loop = true;
      this.video.controls = false;
      this.video.setAttribute("muted", "");
      this.video.setAttribute("playsinline", "");
      this.video.setAttribute("webkit-playsinline", "");
      this.video.removeAttribute("controls");
      this.video.setAttribute("preload", "auto");

      this.video.addEventListener("loadedmetadata", this.forcePlay);
      this.video.addEventListener("canplay", this.forcePlay);
      this.video.addEventListener("ended", this.forcePlay);

      this.forcePlay();
    }

    forcePlay() {
      if (!this.video) return;

      const playPromise = this.video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          this.attachPlaybackUnlockHandlers();
        });
      }
    }

    attachPlaybackUnlockHandlers() {
      if (this.unlockHandlersAttached) return;

      this.unlockHandlersAttached = true;
      document.addEventListener("touchstart", this.resumePlayback, {
        once: true,
        passive: true,
      });
      document.addEventListener("pointerdown", this.resumePlayback, {
        once: true,
        passive: true,
      });
      document.addEventListener("click", this.resumePlayback, {
        once: true,
        passive: true,
      });
    }

    resumePlayback() {
      this.unlockHandlersAttached = false;
      this.forcePlay();
    }

    handleViewportChange() {
      if (!this.video || this.video.dataset.hasMobileSource !== "true") return;

      const wasPaused = this.video.paused;
      this.video.load();

      if (!wasPaused) {
        this.forcePlay();
      }
    }

    handleTriggerClick(event) {
      event.preventDefault();
      this.trackClick();

      if (this.scrollEnabled) {
        if (!this.scrollTarget) return;
        this.scrollToTarget();
        return;
      }

      if (!this.formUrl || !this.modal || !this.dialog) return;
      this.openModal();
    }

    scrollToTarget() {
      const target = this.getScrollTarget();
      if (!target) return;

      const stickyBarHeight =
        this.stickyBar?.offsetHeight || this.trigger?.offsetHeight || 0;
      const headerOffset =
        this.getCssVariableValue("--announcement-bar-height") +
        this.getCssVariableValue("--theme-header-height") +
        stickyBarHeight +
        16;
      const top =
        target.getBoundingClientRect().top + window.scrollY - headerOffset;

      this.animateScrollTo(Math.max(top, 0));
    }

    getScrollTarget() {
      if (!this.scrollTarget) return null;

      const targetId = this.scrollTarget.replace(/^#/, "");
      return (
        document.getElementById(targetId) ||
        document.querySelector(`[data-section-anchor="${targetId}"]`)
      );
    }

    getCssVariableValue(name) {
      const value = window
        .getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();

      return Number.parseFloat(value) || 0;
    }

    animateScrollTo(targetTop) {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        window.scrollTo(0, targetTop);
        return;
      }

      const startTop = window.scrollY;
      const distance = targetTop - startTop;

      if (Math.abs(distance) < 2) {
        window.scrollTo(0, targetTop);
        return;
      }

      const duration = Math.min(2600, Math.max(1400, Math.abs(distance) * 1.1));
      let startTime = null;

      const easeInOutQuint = (progress) => {
        if (progress < 0.5) {
          return 16 * Math.pow(progress, 5);
        }

        return 1 - Math.pow(-2 * progress + 2, 5) / 2;
      };

      const step = (timestamp) => {
        if (startTime === null) startTime = timestamp;

        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeInOutQuint(progress);

        window.scrollTo(0, startTop + distance * easedProgress);

        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };

      window.requestAnimationFrame(step);
    }

    async trackClick() {
      if (
        !window.eventStreaming ||
        typeof window.eventStreaming.prepareCommonDetails !== "function" ||
        typeof window.eventStreaming.dispatchCustomEvent !== "function"
      ) {
        return;
      }

      const customData = await window.eventStreaming.prepareCommonDetails();
      customData.buttonName =
        this.trigger?.getAttribute("data-event-name") || "Sticky Video CTA Buy Now";
      customData.buttonAction =
        this.trigger?.getAttribute("data-event-type") || "click";
      window.eventStreaming.dispatchCustomEvent("BUTTON_CLICK", customData);
    }

    openModal() {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      this.lastFocusedElement = document.activeElement;

      if (this.iframe && !this.iframe.src) {
        this.iframe.src = this.formUrl;
      }

      this.lockScroll();
      this.modal.hidden = false;
      this.modal.setAttribute("aria-hidden", "false");

      window.requestAnimationFrame(() => {
        this.modal.classList.add("is-open");
      });

      document.addEventListener("keydown", this.handleKeydown);

      const target = this.getFocusableElements()[0] || this.dialog;
      if (target) target.focus();
    }

    handleCloseClick(event) {
      event.preventDefault();
      this.closeModal();
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;

      this.modal.classList.remove("is-open");
      this.modal.setAttribute("aria-hidden", "true");
      document.removeEventListener("keydown", this.handleKeydown);

      this.closeTimer = window.setTimeout(() => {
        if (!this.modal) return;

        this.modal.hidden = true;
        this.unlockScroll();
        this.closeTimer = null;

        if (
          this.lastFocusedElement &&
          typeof this.lastFocusedElement.focus === "function"
        ) {
          this.lastFocusedElement.focus();
        }
      }, 240);
    }

    handleKeydown(event) {
      if (!this.modal || this.modal.hidden) return;

      if (event.key === "Escape") {
        event.preventDefault();
        this.closeModal();
        return;
      }

      if (event.key !== "Tab") return;

      const focusableElements = this.getFocusableElements();
      if (!focusableElements.length) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    getFocusableElements() {
      if (!this.modal) return [];

      return Array.from(this.modal.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => {
          if (element.hasAttribute("disabled")) return false;
          if (element.getAttribute("aria-hidden") === "true") return false;

          return (
            element.offsetWidth > 0 ||
            element.offsetHeight > 0 ||
            element === document.activeElement
          );
        },
      );
    }

    lockScroll() {
      this.scrollY = window.scrollY;
      document.documentElement.classList.add("sticky-video-cta-scroll-lock");
      document.body.classList.add("sticky-video-cta-scroll-lock");
      document.body.style.position = "fixed";
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    }

    unlockScroll() {
      document.documentElement.classList.remove("sticky-video-cta-scroll-lock");
      document.body.classList.remove("sticky-video-cta-scroll-lock");
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      window.scrollTo(0, this.scrollY || 0);
    }
  }

  function getSectionInstance(root) {
    if (!root || !(root instanceof Element)) return null;

    if (root.__stickyVideoCta) {
      return root.__stickyVideoCta;
    }

    if (root.dataset.stickyVideoCtaInitialized === "true") {
      delete root.dataset.stickyVideoCtaInitialized;
    }

    if (root.matches(SECTION_SELECTOR)) {
      root.__stickyVideoCta = new StickyVideoCta(root);
      return root.__stickyVideoCta || null;
    }

    const section = root.closest(SECTION_SELECTOR);
    if (!section) return null;

    if (section.dataset.stickyVideoCtaInitialized === "true" && !section.__stickyVideoCta) {
      delete section.dataset.stickyVideoCtaInitialized;
    }

    section.__stickyVideoCta = new StickyVideoCta(section);
    return section.__stickyVideoCta || null;
  }

  function initStickyVideoCtas(scope) {
    const root = scope instanceof Element ? scope : document;

    if (root.matches && root.matches(SECTION_SELECTOR) && !root.__stickyVideoCta) {
      getSectionInstance(root);
    }

    root.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (!section.__stickyVideoCta) {
        getSectionInstance(section);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initStickyVideoCtas());
  } else {
    initStickyVideoCtas();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initStickyVideoCtas(event.target);
  });

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-sticky-video-cta-trigger]");
    if (!trigger || trigger.disabled) return;

    const instance = getSectionInstance(trigger);
    if (!instance || instance.trigger !== trigger) return;

    if (instance.scrollEnabled) return;
    if (!instance.modal || !instance.dialog || !instance.formUrl) return;
    if (!instance.modal.hidden) return;

    event.preventDefault();
    instance.trackClick();
    instance.openModal();
  });
})()
